<template>
  <span v-if="hasEffect" class="nota-comercial-amount-net text-muted">
    <span :class="effect < 0 ? 'text-success' : 'text-danger'">
      {{ effect < 0 ? "−" : "+" }}{{ formatAmount(Math.abs(effect)) }}
    </span>
    <i class="fas fa-arrow-right mx-1" aria-hidden="true"></i>
    <strong class="text-body">{{ formatAmount(netAmount) }}</strong>
    <i
      class="fas fa-file-invoice ms-1"
      aria-hidden="true"
      :title="tooltip"
    ></i>
  </span>
</template>

<script>
import { formatNumberSync } from "@/utils/numberFormatter";
import {
  expenseEffectOfNotes,
  notasComercialesByIds,
} from "../utils/notaComercialByIds";

/**
 * Importe de un gasto después de las notas comerciales que cuelgan de él.
 *
 * Es solo la foto al lado del número: no cambia el importe guardado ni ningún cálculo.
 * Sin notas con efecto no renderiza nada.
 */
export default {
  name: "NotaComercialAmountNet",
  props: {
    /** Ids (o referencias pobladas) de las notas a mirar. */
    noteIds: {
      type: Array,
      default: () => [],
    },
    /** Importe sobre el que se aplica el efecto. */
    amount: {
      type: [Number, String],
      default: 0,
    },
    /** Embarque de la línea: acota el efecto a lo aplicado ahí, no al total de la nota. */
    shippingId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      notes: [],
    };
  },
  computed: {
    effect() {
      return expenseEffectOfNotes(this.notes);
    },
    hasEffect() {
      return Math.abs(this.effect) > 0.009;
    },
    netAmount() {
      return (Number(this.amount) || 0) + this.effect;
    },
    tooltip() {
      return this.notes
        .map((note) =>
          this.$t("notaComercial.pending.badgeLine", {
            name: note.number || note.name || "-",
            amount: this.formatAmount(
              Number.isFinite(Number(note.appliedAmount))
                ? Number(note.appliedAmount)
                : note.amount
            ),
          })
        )
        .join("\n");
    },
    /** Los ids en texto: el watcher tiene que ver el cambio, no la identidad del array. */
    noteIdsKey() {
      return (Array.isArray(this.noteIds) ? this.noteIds : [])
        .map((n) => (n && typeof n === "object" ? String(n._id || "") : String(n || "")))
        .filter(Boolean)
        .join(",");
    },
  },
  watch: {
    noteIdsKey: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    shippingId() {
      this.load();
    },
  },
  methods: {
    async load() {
      const key = this.noteIdsKey;
      if (!key) {
        this.notes = [];
        return;
      }
      const notes = await notasComercialesByIds(key.split(","), this.shippingId);
      // Entre el pedido y la respuesta la fila pudo cambiar de notas.
      if (this.noteIdsKey !== key) return;
      this.notes = notes;
    },
    /** Mismo formato que el resto de los importes de la pantalla. */
    formatAmount(value) {
      return formatNumberSync(Number(value) || 0, null, true);
    },
  },
};
</script>

<style scoped>
.nota-comercial-amount-net {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.85em;
  white-space: nowrap;
}
</style>
